import { useCallback, useEffect } from 'react';
import { useSetRecoilState, useRecoilState } from 'recoil';
import { DESTINATION_ANALYTICS_EVENTS } from '@/analytics/destination';
import { useToast } from '../../hooks/use-toast';

import { endpoints } from '../../constants/index';
import {
  DestinationsState,
  LoadedDestinationState,
  LoadingDestinationState,
  RawDestinationsState,
} from './states';
import { useNetwork } from '../../hooks/use-network';
import {
  initGoogleKeys,
  trackEvent,
} from '../../helpers/external-tools-helper';
import { ConnectionQuery } from '../../queries/connection';

export const useDestinations = () => {
  const googleKeys: any = initGoogleKeys();
  const { get, data: destinations } = useNetwork();
  const { remove: disconnectApi, data: disconnectDestinations } = useNetwork();
  const { post } = useNetwork();
  const [destination, setDestination] = useRecoilState(DestinationsState);
  const setRawDestination = useSetRecoilState(RawDestinationsState);
  const setIsLoading = useSetRecoilState(LoadingDestinationState);
  const setIsLoaded = useSetRecoilState(LoadedDestinationState);
  const invalidateDestinations = ConnectionQuery.useInvalidateDestinations();
  const { toast } = useToast();
  const {
    data: destinationsFromReactQuery,
  } = ConnectionQuery.useDestinations();

  const { destinations: destinationsUrl, destinationProviders } = endpoints();

  useEffect(() => {
    if (destinations?.length) {
      setDestination(destinations[0]);
      setRawDestination(destinations[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinations]);

  const getDestinations = useCallback(async () => {
    setIsLoading(true);
    const destinationsList = await get(destinationProviders);
    setIsLoaded(true);
    if (destinationsList?.length) {
      setDestination(destinationsList[0]);
      setRawDestination(destinationsList[0]);
    }
    setIsLoading(false);
  }, [
    destinationProviders,
    get,
    setDestination,
    setIsLoaded,
    setIsLoading,
    setRawDestination,
  ]);

  const removeDuplicateFolders = useCallback(
    async (folders: any[]) => {
      if (!destinationsFromReactQuery?.destinationAccounts) {
        // Wait for data to be available
        await invalidateDestinations();
        return;
      }

      // Filter out folders that already exist in destinationAccounts
      const uniqueFolders = folders.filter((folder: any) => {
        const isDuplicate = destinationsFromReactQuery.destinationAccounts.some(
          dest => dest.data.id === folder.id
        );
        return !isDuplicate;
      });

      if (uniqueFolders.length === 0) {
        // No new folders to add
        toast({
          title: 'Duplicate Folders',
          description:
            'These folders are already connected to your destination.',
          variant: 'destructive',
        });
        return;
      }

      const apis = uniqueFolders.map((folder: any) =>
        post(destinationsUrl, {
          destinationProviderId: destinationsFromReactQuery.id,
          data: folder,
        })
      );
      setIsLoading(true);

      await Promise.all(apis);
      // await get(destinationProviders);
      trackEvent(DESTINATION_ANALYTICS_EVENTS.DESTINATION_FOLDER_SELECTED, {
        destinationId: destination.id,
        selectedFolders: folders,
      });
      setIsLoading(false);
      invalidateDestinations();
    },
    [
      destinationsFromReactQuery,
      destinationsUrl,
      post,
      setIsLoading,
      invalidateDestinations,
      toast,
    ]
  );

  const fetchGoogleFolders = useCallback(
    (token: string) => {
      if (window.google?.picker) {
        const googleViewId = window.google.picker.ViewId.FOLDERS;
        const docsView = new window.google.picker.DocsView(googleViewId)
          .setIncludeFolders(true)
          .setMimeTypes(googleKeys?.mimeType)
          .setSelectFolderEnabled(true);
        const picker = new window.google.picker.PickerBuilder()
          .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
          .addView(docsView)
          .setOAuthToken(token)
          .setDeveloperKey(googleKeys?.developerKey)
          .setCallback(fold => {
            if (fold.docs) {
              removeDuplicateFolders(fold.docs);
            }
          });
        picker.build().setVisible(true);
      }
    },
    [googleKeys?.developerKey, googleKeys?.mimeType, removeDuplicateFolders]
  );

  const disconnect = useCallback(async () => {
    await disconnectApi(`${destinationProviders}/${destination.id}`);
  }, [destinationProviders, destination.id, disconnectApi]);

  useEffect(() => {
    if (disconnectDestinations) {
      setDestination({} as any);
      setRawDestination({} as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disconnectDestinations]);

  return {
    getDestinations,
    fetchGoogleFolders,
    disconnect,
  };
};
